// Task Recurrence Types - Microsoft Outlook Style
// Matches the database schema for task recurrence

export type RecurrencePattern = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

type RecurrenceEndType = 'never' | 'after_occurrences' | 'by_date';

type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Sunday, 6=Saturday

type WeekOfMonth = 1 | 2 | 3 | 4 | 5; // 1=first, 2=second, 3=third, 4=fourth, 5=last

interface RecurrenceConfig {
  // Basic pattern
  pattern: RecurrencePattern;
  interval: number; // Every N days/weeks/months/years
  
  // Weekly pattern
  daysOfWeek?: DayOfWeek[]; // For weekly: which days of the week
  
  // Monthly pattern
  dayOfMonth?: number; // For monthly: specific day (1-31)
  weekOfMonth?: WeekOfMonth; // For monthly: which week
  dayOfWeekInMonth?: DayOfWeek; // For monthly: which day in that week
  
  // Yearly pattern
  monthOfYear?: number; // For yearly: which month (1-12)
  
  // End conditions
  endType: RecurrenceEndType;
  endDate?: string; // ISO date string
  occurrences?: number; // Number of occurrences
}

// Monthly recurrence sub-mode introduced 2026-05-22. The old single-value
// day_of_month / week_of_month fields couldn't express patterns like
// "1st + 3rd Monday" or "days 10, 15, 20" or "before the 25th".
//   day_range     → visible from recurrence_month_range_from through
//                   recurrence_month_range_until (both inclusive, either
//                   side nullable for open-ended ranges)
//   specific_days → visible only on recurrence_specific_days_of_month entries
//   nth_weekdays  → visible on every (ordinal × weekday) intersection in
//                   recurrence_nth_weekdays
// Plan: /Users/gauravarora/.claude/plans/image-9-can-you-bright-blanket.md
export type MonthlyMode = 'day_range' | 'specific_days' | 'nth_weekdays';

// -1 in the ordinals array represents "Last" of that weekday in the month.
interface NthWeekdaysSelection {
  ordinals: number[];   // values in {1, 2, 3, 4, -1}
  weekdays: number[];   // values in {0..6}; matches DayOfWeek convention
}

type CompletionScope = 'per_employee' | 'per_store';

export interface TaskRecurrence {
  recurrence_pattern: RecurrencePattern;
  recurrence_interval: number;
  recurrence_days_of_week?: number[];
  recurrence_day_of_month?: number;
  recurrence_week_of_month?: number;
  recurrence_month_of_year?: number;
  recurrence_end_type: RecurrenceEndType;
  recurrence_end_date?: string;
  recurrence_occurrences?: number;
  recurrence_config?: RecurrenceConfig;
  // New monthly sub-mode + per-mode payload. Only meaningful when
  // recurrence_pattern === 'monthly'. Older tasks without the migration
  // backfill leave recurrence_monthly_mode null and fall back to the legacy
  // day_of_month / week_of_month branches in isTaskScheduledForDate.
  recurrence_monthly_mode?: MonthlyMode | null;
  recurrence_month_range_from?: number | null;
  recurrence_month_range_until?: number | null;
  recurrence_specific_days_of_month?: number[] | null;
  recurrence_nth_weekdays?: NthWeekdaysSelection | null;
  // Once-per-period scope dimension. Drives whether the "Done this period"
  // compliance flag is computed across all submissions (per_employee) or
  // per assigned store (per_store).
  completion_scope_dimension?: CompletionScope;
}

// Helper functions for recurrence
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
] as const;

export const WEEKS_OF_MONTH = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: 5, label: 'Last' },
] as const;

export const MONTHS_OF_YEAR = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
] as const;

// Format recurrence as human-readable text (Outlook-style)
export function formatRecurrenceText(recurrence: Partial<TaskRecurrence>): string {
  if (!recurrence.recurrence_pattern || recurrence.recurrence_pattern === 'none') {
    return 'Does not repeat';
  }

  const interval = recurrence.recurrence_interval || 1;
  let text = '';

  switch (recurrence.recurrence_pattern) {
    case 'daily':
      text = interval === 1 ? 'Daily' : `Every ${interval} days`;
      break;

    case 'weekly':
      if (interval === 1) {
        text = 'Weekly';
      } else {
        text = `Every ${interval} weeks`;
      }
      if (recurrence.recurrence_days_of_week && recurrence.recurrence_days_of_week.length > 0) {
        const days = recurrence.recurrence_days_of_week
          .sort()
          .map(d => DAYS_OF_WEEK[d].short)
          .join(', ');
        text += ` on ${days}`;
      }
      break;

    case 'monthly':
      if (interval === 1) {
        text = 'Monthly';
      } else {
        text = `Every ${interval} months`;
      }
      // Prefer the new sub-mode summary when available; fall back to the
      // legacy single-value fields for pre-migration rows.
      if (recurrence.recurrence_monthly_mode === 'day_range') {
        const f = recurrence.recurrence_month_range_from;
        const u = recurrence.recurrence_month_range_until;
        if (f != null && u != null) text += ` on days ${f}–${u}`;
        else if (f != null) text += ` from day ${f} onwards`;
        else if (u != null) text += ` on days 1–${u}`;
      } else if (recurrence.recurrence_monthly_mode === 'specific_days') {
        const days = (recurrence.recurrence_specific_days_of_month || []).slice().sort((a, b) => a - b);
        if (days.length > 0) text += ` on day${days.length > 1 ? 's' : ''} ${days.join(', ')}`;
      } else if (recurrence.recurrence_monthly_mode === 'nth_weekdays') {
        const sel = recurrence.recurrence_nth_weekdays;
        const ords = (sel?.ordinals || [])
          .map((o) => (o === -1 ? 'Last' : WEEKS_OF_MONTH.find((w) => w.value === o)?.label))
          .filter(Boolean);
        const wks = (sel?.weekdays || []).map((w) => DAYS_OF_WEEK[w]?.short).filter(Boolean);
        if (ords.length > 0 && wks.length > 0) {
          text += ` on the ${ords.join(' + ')} ${wks.join('/')}`;
        }
      } else if (recurrence.recurrence_day_of_month) {
        text += ` on day ${recurrence.recurrence_day_of_month}`;
      } else if (recurrence.recurrence_week_of_month && recurrence.recurrence_days_of_week?.[0] !== undefined) {
        const week = WEEKS_OF_MONTH.find(w => w.value === recurrence.recurrence_week_of_month)?.label;
        const day = DAYS_OF_WEEK[recurrence.recurrence_days_of_week[0]].label;
        text += ` on the ${week} ${day}`;
      }
      break;

    case 'yearly':
      if (interval === 1) {
        text = 'Yearly';
      } else {
        text = `Every ${interval} years`;
      }
      if (recurrence.recurrence_month_of_year) {
        const month = MONTHS_OF_YEAR.find(m => m.value === recurrence.recurrence_month_of_year)?.label;
        text += ` in ${month}`;
        if (recurrence.recurrence_day_of_month) {
          text += ` on day ${recurrence.recurrence_day_of_month}`;
        }
      }
      break;
  }

  // Add end condition
  if (recurrence.recurrence_end_type === 'by_date' && recurrence.recurrence_end_date) {
    const endDate = new Date(recurrence.recurrence_end_date);
    text += `, until ${endDate.toLocaleDateString()}`;
  } else if (recurrence.recurrence_end_type === 'after_occurrences' && recurrence.recurrence_occurrences) {
    text += `, ${recurrence.recurrence_occurrences} times`;
  }

  return text;
}

// Compute the partial TaskRecurrence shape produced when the admin picks a
// new pattern from the dropdown. Centralised here so both RecurrenceSelector
// (when it owns the pattern picker) and TasksPage (when the picker is
// pulled out into the same row as Assign to Roles) write back the same
// canonical defaults. Mirrors the logic that used to live inline in
// RecurrenceSelector.handlePatternChange.
//
// `anchorDate` is the task's Start Date — used to default the weekly day
// chips to whichever day that date falls on, so a new weekly task always
// starts already showing on its first occurrence.
export function defaultsForPattern(
  pattern: RecurrencePattern,
  anchorDate?: string,
): Partial<TaskRecurrence> {
  const next: Partial<TaskRecurrence> = {
    recurrence_pattern: pattern,
    recurrence_interval: 1,
    recurrence_end_type: 'never',
  };
  const anchor = anchorDate ? new Date(anchorDate) : new Date();

  if (pattern === 'weekly') {
    next.recurrence_days_of_week = [anchor.getDay()];
  } else if (pattern === 'monthly') {
    // Default to day_range with no bounds — admin must fill in From / Until
    // explicitly. All other monthly-mode payload columns NULL so a fresh
    // pattern switch starts clean.
    next.recurrence_monthly_mode = 'day_range';
    next.recurrence_month_range_from = null;
    next.recurrence_month_range_until = null;
    next.recurrence_specific_days_of_month = null;
    next.recurrence_nth_weekdays = null;
    next.recurrence_day_of_month = undefined;
    next.recurrence_week_of_month = undefined;
  } else if (pattern === 'yearly') {
    // Yearly is hidden from the picker but kept here so existing yearly
    // tasks remain editable end-to-end. Mirrors prior inline defaults.
    next.recurrence_month_of_year = anchor.getMonth() + 1;
    next.recurrence_day_of_month = anchor.getDate();
  }
  return next;
}

// Validate recurrence configuration
export function validateRecurrence(recurrence: Partial<TaskRecurrence>): string | null {
  if (!recurrence.recurrence_pattern || recurrence.recurrence_pattern === 'none') {
    return null;
  }

  if (!recurrence.recurrence_interval || recurrence.recurrence_interval < 1) {
    return 'Interval must be at least 1';
  }

  if (recurrence.recurrence_pattern === 'weekly') {
    if (!recurrence.recurrence_days_of_week || recurrence.recurrence_days_of_week.length === 0) {
      return 'Please select at least one day of the week';
    }
  }

  if (recurrence.recurrence_pattern === 'monthly') {
    const mode = recurrence.recurrence_monthly_mode;
    // New mode-driven validation. Pre-migration tasks may have neither the
    // new mode nor the legacy fields set if the admin is mid-edit — accept
    // that transient state, the form's submit handler will catch the final
    // empty save.
    if (mode === 'day_range') {
      const f = recurrence.recurrence_month_range_from;
      const u = recurrence.recurrence_month_range_until;
      if (f == null && u == null) {
        return 'Specify at least a From or Until day for the monthly range';
      }
      if (f != null && (f < 1 || f > 31)) return 'From day must be between 1 and 31';
      if (u != null && (u < 1 || u > 31)) return 'Until day must be between 1 and 31';
      if (typeof f === 'number' && typeof u === 'number' && f > u) {
        return 'From day must be on or before Until day';
      }
    } else if (mode === 'specific_days') {
      const days = recurrence.recurrence_specific_days_of_month;
      if (!Array.isArray(days) || days.length === 0) {
        return 'Pick at least one date for the monthly schedule';
      }
    } else if (mode === 'nth_weekdays') {
      const sel = recurrence.recurrence_nth_weekdays;
      if (!sel || !sel.ordinals?.length || !sel.weekdays?.length) {
        return 'Pick at least one ordinal (First / Second / … / Last) and at least one weekday';
      }
    } else if (!recurrence.recurrence_day_of_month && !recurrence.recurrence_week_of_month) {
      return 'Please specify a monthly schedule';
    }
  }

  if (recurrence.recurrence_pattern === 'yearly') {
    if (!recurrence.recurrence_month_of_year) {
      return 'Please select a month';
    }
  }

  if (recurrence.recurrence_end_type === 'by_date' && !recurrence.recurrence_end_date) {
    return 'Please specify an end date';
  }

  if (recurrence.recurrence_end_type === 'after_occurrences') {
    if (!recurrence.recurrence_occurrences || recurrence.recurrence_occurrences < 1) {
      return 'Please specify number of occurrences';
    }
  }

  return null;
}
